import { useCallback, useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { IlokAccount } from "./IlokAccountsCard"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// The admin User page's iLok section: the user's linked and pending
// iLok.com accounts, each with an Unlink action (app-wide admins only — the
// backend rejects everyone else). Unlinking severs the account in both
// directions (see unlinkIlokAccount in the backend): the entitlements the
// link issued are revoked with their licenses, the licenses deposited into
// the account are revoked on PACE, and the link row goes. `onUnlinked`
// lets the page refresh its entitlement and license tables.
export default function UserIlokAccounts({ userId, accessToken, onUnlinked }: {
  userId: string
  accessToken: string
  onUnlinked: () => void
}) {
  const [accounts, setAccounts] = useState<IlokAccount[] | undefined>(undefined)
  const [loadError, setLoadError] = useState(false)
  const [unlinking, setUnlinking] = useState<IlokAccount | null>(null)

  const load = useCallback(() => {
    Api.getUserIlokAccounts({ userId, accessToken })
      .then(({ ilokAccounts }) => setAccounts(ilokAccounts))
      .catch(() => setLoadError(true))
  }, [userId, accessToken])

  useEffect(() => {
    // empty on a hard reload before auth resolves
    if (!accessToken) return
    load()
  }, [accessToken, load])

  if (loadError) return <p className="empty-state">Error loading iLok accounts</p>
  if (accounts === undefined) return <p className="empty-state"><Spinner size={16} /> Loading iLok accounts…</p>
  if (accounts.length === 0) return <p className="empty-state">No iLok accounts linked</p>

  return (
    <>
      <table className="data-table">
        <thead>
          <tr>
            <th>iLok User ID</th>
            <th>iLok email</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <tr key={account.ilokId}>
              <td>{account.ilokId}</td>
              <td>{account.ilokEmail}</td>
              <td>
                {account.confirmed
                  ? <span className="badge">linked</span>
                  : <span className="badge badge-trial">pending</span>}
              </td>
              <td style={{ textAlign: "right" }}>
                <button
                  className="card-link-danger"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  onClick={() => setUnlinking(account)}
                >
                  Unlink
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {unlinking && (
        <UnlinkIlokDialog
          userId={userId}
          account={unlinking}
          accessToken={accessToken}
          onClose={() => setUnlinking(null)}
          onUnlinked={() => { setAccounts((prev) => prev?.filter((a) => a.ilokId !== unlinking.ilokId)); onUnlinked() }}
        />
      )}
    </>
  )
}

// Confirmation + result for one unlink. The warning spells out both sides
// of the severance; the result reports what actually went. Errors stay
// inline (the dialog is the dismissable surface).
function UnlinkIlokDialog({ userId, account, accessToken, onClose, onUnlinked }: {
  userId: string
  account: IlokAccount
  accessToken: string
  onClose: () => void
  onUnlinked: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "unlinking" | "error" | "success">("confirm")
  const [errorMessage, setErrorMessage] = useState("")
  const [result, setResult] = useState<{ revokedEntitlementIds: string[], deletedLicenseIds: string[], revokedIlokLicenseGuids: string[] } | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleUnlink = async () => {
    setStep("unlinking")
    setErrorMessage("")
    try {
      const outcome = await Api.unlinkUserIlokAccount({ userId, ilokId: account.ilokId, accessToken })
      setResult(outcome)
      onUnlinked()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "unlinking") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close" disabled={step === "unlinking"}>&times;</button>
        <h2 className="redeem-dialog-title">Unlink iLok Account</h2>

        {step !== "success" && (
          <>
            <p>
              This unlinks iLok account <strong>{account.ilokId}</strong> from <strong>{userId}</strong>.
            </p>
            {account.confirmed ? (
              <ul style={{ paddingLeft: 20, margin: "0 0 12px" }}>
                <li>
                  Every entitlement issued from the account's iLok licenses is <strong>revoked and its licenses and
                  activations deleted</strong>. The licenses stay in the user's iLok account.
                </li>
                <li>
                  Every license deposited into this iLok account from the user's own entitlements is{" "}
                  <strong>revoked on iLok</strong>. The entitlements stay and can be deposited again after re-linking.
                </li>
              </ul>
            ) : (
              <p>The link is still pending; its verification email stops working.</p>
            )}
            <p>Linking the account again (to this user or another) re-issues its licenses. This cannot be undone.</p>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={step === "unlinking"}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={step === "unlinking"}
                onClick={handleUnlink}
              >
                {step === "unlinking" ? <><Spinner size={12} /> Unlinking…</> : "Unlink"}
              </button>
            </div>
          </>
        )}

        {step === "success" && result && (
          <>
            <p className="generate-codes-success">
              iLok account {account.ilokId} unlinked: {count(result.revokedEntitlementIds.length, "entitlement")} revoked,{" "}
              {count(result.deletedLicenseIds.length, "license")} deleted, {count(result.revokedIlokLicenseGuids.length, "deposited iLok license")} revoked.
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
